import { firmSummaryUrl, fundOwnershipUrl } from 'common/config/urls';
import { Row, Rows, RowWithCategory } from 'common/types/scalarSpreadsheet';
import { ValueWithLinkViewer } from 'components';
import {
  COMPANY_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
  COMPANY_SUMMARY_SPREADSHEET_TABLE,
  SHEET_ALIASES_CONSTANTS,
  SHEET_TITLES_CONSTANTS,
} from 'pages/Portfolio/common/constants/companySummary';
import { createColumnKeyName } from 'pages/Portfolio/pages/CompanySummary/config/createColumns';
import { generateColumnKey, getArrayValue, getObjectValue, getStringValue } from 'utilities';
import { CreateSecuritiesRowsParams, RowConfigParams, RowData } from './types';
import {
  COMPANY_SUMMARY_SPREADSHEET_FUND_TITLE,
  COMPANY_SUMMARY_SPREADSHEET_FUND_TOTAL,
} from '../../../../common/constants/companySummary/sheetAliases';
import { InvestmentsWithId } from '../../../../hooks/useCompanySummarySheet/types';

const createSecuritiesRows = (params: CreateSecuritiesRowsParams): RowWithCategory[] => {
  const { companyUrl, fundSlug, investments: securities } = params;

  securities.sort((a: InvestmentsWithId, b: InvestmentsWithId) => {
    if (a.security_name && b.security_name) {
      return a.security_name.localeCompare(b.security_name);
    }
    return 0;
  });

  return getArrayValue(
    securities?.map(security => {
      const securityName = getStringValue(security?.security_name);

      return {
        alias: generateColumnKey({
          id: security.id,
          name: createColumnKeyName({ name: securityName, slug: fundSlug }),
          prefix: COMPANY_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
        }),
        // Carried so the parent security subtotal row in
        // createRowsFromInvestments can stamp { fund, security_id } onto its
        // own data — that's where the per-security CFL bucket gets applied.
        data: { fund: fundSlug, security_id: security?.security_id ?? null },
        gridType: 'string',
        readOnly: true,
        category: `security_${fundSlug}-${securityName}`,
        value: <ValueWithLinkViewer name={securityName} url={companyUrl} />,
      } as unknown as RowWithCategory;
    })
  );
};

const getTitlesRows = (params: Rows): Rows => {
  const titleParams = params.filter(fund => !fund.alias?.includes('security_'));

  return titleParams;
};

// Function to create the list with parent-child relationship and add 'value' field
const createRowsFromInvestments = (params: RowWithCategory[]): Rows => {
  const dataParams = params.filter(fund => fund.alias?.includes('security_'));

  const newRowsList: { [key: string]: Row } = {};

  const newParams = [...dataParams];

  // showSubRows is true if there are two or more tranches per security
  const showSubRows = (category: string) => {
    const tranches = dataParams.filter(item => item.category === category);
    return tranches.length > 1;
  };

  dataParams.forEach((item, index) => {
    const { value, category } = item;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const newValue = value?.props?.name ?? value;
    const newAlias = category ?? '';
    // Inherit { fund, security_id } from the leaf so the parent subtotal row
    // can pull its per-security CFL bucket in cellParser. All siblings share
    // a fund and a security_id, so picking it off any leaf is fine.
    const leafData = (item.data as RowData | undefined) ?? undefined;
    newRowsList[newAlias] = {
      alias: newAlias,
      value: newValue,
      gridType: item.gridType,
      hasSubRows: true,
      hidden: false,
      allowNegativeValue: true,
      readOnly: true,
      showSubRows: showSubRows(category as string),
      className: 'bold-row',
      data: leafData,
    } as Row<RowData>;
    newParams[index] = { ...item, parent: newAlias, style: { paddingLeft: '1rem' } };
  });

  // Combine both arrays
  return [...Object.values(newRowsList), ...newParams];
};

function organizeRows(parentChildRows: RowWithCategory[], titles: RowWithCategory[]): RowWithCategory[] {
  const organizedRows: RowWithCategory[] = [];

  // Group parentChildRows by their parent (for child rows) and alias (for parent rows)
  const parentGroups: { [parentAlias: string]: RowWithCategory[] } = {};
  const childGroups: { [parentAlias: string]: RowWithCategory[] } = {};

  parentChildRows.forEach(row => {
    if (row.hasSubRows) {
      const rowValue = row.value?.toString() ?? '';
      const alias = row?.alias?.toString().replace(rowValue, '') ?? '';
      // Parent rows
      if (!parentGroups[alias]) {
        parentGroups[alias] = [];
      }
      parentGroups[alias].push(row);
    } else {
      const category = row?.category ?? '';
      // Child rows (sons)
      if (!childGroups[category]) {
        childGroups[category] = [];
      }
      childGroups[category].push(row);
    }
  });

  const titleRows = titles.filter(row => row.alias === COMPANY_SUMMARY_SPREADSHEET_FUND_TITLE);
  const totalRows = titles.filter(row => row.alias === COMPANY_SUMMARY_SPREADSHEET_FUND_TOTAL);

  // Now iterate over each title and organize it with its parent and child rows
  titleRows.forEach(titleRow => {
    organizedRows.push(titleRow);

    // Find parents related to the title's category

    const relevantParents = parentChildRows.filter(row => {
      const rowValue = row.value?.toString() ?? '';
      const parentAlias = row.alias?.replace(`-${rowValue}`, '') ?? ''; // Remove the title alias part from the row alias to get the "parent"
      return parentAlias === titleRow.category && row.hasSubRows === true; // This checks if the row alias matches the title
    });

    relevantParents.forEach(parentRow => {
      organizedRows.push(parentRow);
      const rowAlias = parentRow.alias?.toString() ?? '';
      const children = childGroups[rowAlias] || [];
      children.forEach(childRow => organizedRows.push(childRow));
    });

    // Add total row if exists in parentChildRows under this title's category
    const totalRow = totalRows.find(row => row.category === titleRow.category);
    if (totalRow) {
      organizedRows.push(totalRow);
    }
  });

  return organizedRows;
}

function rearrangeItems(items: Rows): Rows {
  const parents: Rows = [];
  const children: Rows = [];

  // Separate parents and children
  items.forEach(item => {
    if (item.hasSubRows) {
      parents.push(item);
    } else {
      children.push(item);
    }
  });

  // Create a map to easily find parents by their alias
  const parentMap: { [key: string]: Row } = {};
  parents.forEach(parent => {
    if (parent.alias) {
      parentMap[parent.alias] = parent;
    }
  });

  // Create a new array to hold the rearranged items
  const rearrangedItems: Rows = [];

  // Add parents first, followed by their respective children
  parents.forEach(parent => {
    rearrangedItems.push(parent);
    // Find children for this parent
    children.forEach(child => {
      if (child.parent === parent.alias) {
        rearrangedItems.push(child);
      }
    });
  });

  return rearrangedItems;
}

const rowConfig = (params: RowConfigParams): RowWithCategory[] => {
  const { companySlug, firmSlug, funds, queryParams } = params;
  const measurementDate = new URLSearchParams(queryParams.replace(/^\?/, '')).get('date') ?? undefined;

  // Get Company Fund Ownership URL
  const companyUrl = `${fundOwnershipUrl(firmSlug, companySlug)}${queryParams}`;

  // Get Funds Securities
  const fundsSecurities = (funds || []).reduce((accumulator: any, current) => {
    const { fund_id: fundId, fund_name: fundName, fund_slug: currentFundSlug, investments } = getObjectValue(current);

    const fundSlug = getStringValue(currentFundSlug);
    const fundUrl = fundId ? firmSummaryUrl(firmSlug, measurementDate, String(fundId)) : '';

    const currentFund = [
      // Fund Title
      {
        alias: SHEET_ALIASES_CONSTANTS.COMPANY_SUMMARY_SPREADSHEET_FUND_TITLE,
        className: `${COMPANY_SUMMARY_SPREADSHEET_TABLE}-fund-title-header subheader-division`,
        gridType: 'string',
        hidden: true,
        readOnly: true,
        category: `security_${fundSlug}`,
        value: <ValueWithLinkViewer name={getStringValue(fundName)} isDarkMode url={fundUrl} />,
      } as RowWithCategory,
      // Fund Securities
      ...createSecuritiesRows({ companyUrl, fundSlug, investments }),
      // Fund Total
      {
        alias: SHEET_ALIASES_CONSTANTS.COMPANY_SUMMARY_SPREADSHEET_FUND_TOTAL,
        className: `${COMPANY_SUMMARY_SPREADSHEET_TABLE}-fund-total subtotal-division`,
        data: { fund: fundSlug },
        format: null,
        gridType: 'string',
        readOnly: true,
        category: `security_${fundSlug}`,
        value: SHEET_TITLES_CONSTANTS.COMPANY_SUMMARY_SPREADSHEET_FUND_TOTAL,
      } as RowWithCategory<RowData>,
    ];

    return [...accumulator, ...currentFund];
  }, [] as RowWithCategory[]) || [];

  const titlesRows = getTitlesRows(fundsSecurities);
  const updatedFundsSecurities = createRowsFromInvestments(fundsSecurities);
  const securitiesOrdered = rearrangeItems(updatedFundsSecurities);
  const filteredSecurities = securitiesOrdered.filter(child => {
    if (!child.parent) {
      // Keep objects without a parent (titles and totals)
      return true;
    }

    const siblings = securitiesOrdered.filter(item => item.parent === child.parent);
    // Keep only if there are two or more tranches
    return siblings.length > 1;
  });

  const securitiesGroupedByFund = organizeRows(filteredSecurities, titlesRows);

  return [
    // Header title
    {
      alias: SHEET_ALIASES_CONSTANTS.COMPANY_SUMMARY_SPREADSHEET_HEADER_TITLE,
      className: `${COMPANY_SUMMARY_SPREADSHEET_TABLE}-header division-bottom-only`,
      gridType: 'string',
      largeHeader: true,
      readOnly: true,
      value: SHEET_TITLES_CONSTANTS.COMPANY_SUMMARY_SPREADSHEET_HEADERS[0].value,
    } as RowWithCategory,
    // Fund Securities
    ...securitiesGroupedByFund,
    // Firm Total
    {
      alias: SHEET_ALIASES_CONSTANTS.COMPANY_SUMMARY_SPREADSHEET_FIRM_TOTAL,
      className: `${COMPANY_SUMMARY_SPREADSHEET_TABLE}-firm-total division`,
      format: null,
      gridType: 'string',
      readOnly: true,
      value: SHEET_TITLES_CONSTANTS.COMPANY_SUMMARY_SPREADSHEET_FIRM_TOTAL,
    } as RowWithCategory,
  ];
};

export default rowConfig;
